import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { Spinner } from '@/shared/components/Spinner';
import { LoginView } from '@/modules/auth/views/LoginView';
import { RequireAuth } from './guards/RequireAuth';
import { AppShell } from './layouts/AppShell';
import styles from './router.module.scss';

/*
 * As telas internas entram sob demanda.
 *
 * Antes tudo vinha num pacote só: quem abria o login baixava a agenda, os
 * prontuários, os modais de impressão e a galeria de componentes junto — e a
 * primeira tela é justamente aquela em que ninguém ainda sabe se vai usar o
 * resto. O login fica estático de propósito: é a porta de entrada, e dividir a
 * porta em duas idas à rede atrasaria exatamente quem tem menos paciência.
 */
const ScheduleView = lazy(async () => ({
  default: (await import('@/modules/schedule/views/ScheduleView')).ScheduleView,
}));
const PatientListView = lazy(async () => ({
  default: (await import('@/modules/patients/views/PatientListView')).PatientListView,
}));
const PatientFormView = lazy(async () => ({
  default: (await import('@/modules/patients/views/PatientFormView')).PatientFormView,
}));
const MedicalRecordView = lazy(async () => ({
  default: (await import('@/modules/medical-records/views/MedicalRecordView')).MedicalRecordView,
}));
const UserListView = lazy(async () => ({
  default: (await import('@/modules/users/views/UserListView')).UserListView,
}));
const UserFormView = lazy(async () => ({
  default: (await import('@/modules/users/views/UserFormView')).UserFormView,
}));
const WorkingHoursView = lazy(async () => ({
  default: (await import('@/modules/users/views/WorkingHoursView')).WorkingHoursView,
}));
const ComponentGallery = lazy(async () => ({
  default: (await import('./pages/ComponentGallery')).ComponentGallery,
}));

/** Espera do carregamento sob demanda — visível apenas na primeira visita à tela. */
function comCarregamento(tela: ReactElement): ReactElement {
  return (
    <Suspense
      fallback={
        <div className={styles.carregando} role="status">
          <Spinner label="Carregando a tela" />
        </div>
      }
    >
      {tela}
    </Suspense>
  );
}

/**
 * Rotas da aplicação.
 *
 * As rotas internas são filhas de um único `AppShell` protegido por
 * `RequireAuth`: a navegação, o `<main>` e o skip link ficam num lugar só, e não
 * dependem de cada tela lembrar de incluí-los. `/login` e `/design-system` são
 * as únicas públicas e ficam fora do esqueleto.
 */
const router = createBrowserRouter([
  { path: '/login', element: <LoginView /> },
  { path: '/design-system', element: comCarregamento(<ComponentGallery />) },

  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: '/agenda', element: comCarregamento(<ScheduleView />) },
      { path: '/prontuarios', element: comCarregamento(<PatientListView />) },
      // A ficha clínica do paciente — as fichas de atendimento e o histórico.
      { path: '/prontuarios/:id/prontuario', element: comCarregamento(<MedicalRecordView />) },
      // `/prontuarios/novo` cai aqui também: o formulário trata `novo` como criação.
      { path: '/prontuarios/:id', element: comCarregamento(<PatientFormView />) },

      { path: '/configuracoes', element: <Navigate to="/configuracoes/usuarios" replace /> },
      { path: '/configuracoes/usuarios', element: comCarregamento(<UserListView />) },
      // `/configuracoes/usuarios/novo` cai aqui: o formulário trata `novo` como criação.
      { path: '/configuracoes/usuarios/:id', element: comCarregamento(<UserFormView />) },
      { path: '/configuracoes/horarios', element: comCarregamento(<WorkingHoursView />) },
    ],
  },

  { path: '/', element: <Navigate to="/agenda" replace /> },
  { path: '*', element: <Navigate to="/agenda" replace /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
